import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { AgGridReact } from "ag-grid-react";
import _ from "lodash";
import "../../../common/style.css";
import "ag-grid-enterprise";
import Customtooltip from "../../CustomTooltip/index";
import { onGridSizeChanged } from "../../AgGrid/grid-util";
import { constants } from "../../../common/constants";
import { getRowStyle } from "../../../common/commonFunctions";
import { ContractReturnDef } from "../../../common/columnContractReturn";
import { ContractReturnCallbacksDef } from "../../../common/columnContractReturnCallbacks";

interface AgGridProps {
  rowData: any;
  height?: any;
  type?: string;
  onCellChange?: any;
  getGridApi?:any;
  getColumnApi?:any;
  setSelectedRows?:any;
  setResetFullReturn?:any
}

const ContractReturnGrid = ({
  rowData,
  height,
  onCellChange,
  getGridApi,
  getColumnApi,
  type,
  setSelectedRows,
  setResetFullReturn
}: AgGridProps) => {
  const gridRef = useRef<any>(null);
  let colDefs = () => {
     let columns: any[] = [];

    columns = type === 'Returns'? ContractReturnCallbacksDef : ContractReturnDef
     return columns;

  }
  
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: true,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      tooltipComponent: Customtooltip,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        let isTabKey = params?.event?.key === constants.Tab;
        if (isTabKey) gridRef?.current?.api?.stopEditing();
        return false;
      },
    };
  }, []);

  const [columnDefs] = useState<any>(colDefs);
  let gridApi: any;
  useEffect(() => {
    gridApi = gridRef.current!.api;
    getUpdatedData(rowData);
  }, [rowData]);

  const getUpdatedData = useCallback(
    async (data: any) => {
      await gridApi?.applyTransactionAsync(
        { update: [rowData] },
      );
      gridApi?.refreshCells();
    },
    [rowData]
  );

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit();
    gridApi = params.api;
    getGridApi(gridApi);
    var gridColumnApi = gridRef.current!.columnApi;
    getColumnApi(gridColumnApi);
  };

  const onFirstDataRendered = ()=>{
    focusToEditableColumn();
  }

  const focusToEditableColumn = ()=>{
   
      gridApi?.ensureIndexVisible(0);
        
      // scrolls to the first column
      var gridColumnApi = gridRef.current!.columnApi;
      const columnList = gridColumnApi?.getAllDisplayedColumns();
      let column = columnList?.find((x:any)=>x?.colId === 'newValue');
      if(column){
        gridApi?.ensureColumnVisible(column);
        // sets focus into the first grid cell
        gridApi?.setFocusedCell(0,column);
      }
    
  }

  const onCellValueChanged = useCallback((event: any) => {
    let updateArray: any = [];
    event.value = _.isString(event?.value) && event?.value?.includes(",")
    ? +(event?.value?.replace(/,/g, ""))
    : +event?.value;
    onCellChange(event.data, event.value, event.column);
    const quantity = +(event.data?.quantity)
     if(quantity !== +event?.value){
     setTimeout(() => {
      var isNodeSelected = gridRef.current!.api?.getRowNode(event?.data?.id)?.selected;
      if(isNodeSelected)
        gridRef.current!.api?.getRowNode(event?.data?.id).setSelected(false);
     }, 200);
    }
     
    updateArray.push(event.data);
    gridApi = gridRef.current!.api;
    gridApi?.applyTransaction({ update: updateArray });
    var params = {
      force: true,
      suppressFlash: true,
      columns: [event.column],
    };

    gridApi.refreshCells(params);
  }, []);


  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.id) {
        return params.data.id;
      }
    }

    return makeid(5);
  };

  function makeid(length: any) {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: "",
    };
  }, []);

  const onGridRowDataChanged = (params: any) => {
    let refreshParams: any = {
      force: true,
      suppressFlash: true,
    };
    gridRef.current!.api.refreshCells(refreshParams);
  };

  const onSelectionChanged = useCallback((params: any) => {
    const selectedRows = gridRef.current!.api.getSelectedRows();
    setSelectedRows(selectedRows);
    setResetFullReturn(selectedRows?.length === 0 && params?.source === 'uiSelectAllFiltered')
  }, []);

  return (
    <div
      className="ag-theme-balham"
      style={{
        height: `${height}vh`,
        width: "100%",
      }}
    >
      <AgGridReact
        ref={gridRef}
        rowStyle={{
          justifyContent: "center",
          color: "#2D2D2D",
          fontSize: "0.75rem",
          fontWeight: "400",
          fontStyle: "normal",
        }}
        rowHeight={22}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        onRowDataUpdated={onGridRowDataChanged}
        rowData={rowData}
        onGridReady={onGridReady}
        onGridSizeChanged={onGridSizeChanged}
        getRowStyle={getRowStyle}
        onCellValueChanged={onCellValueChanged}
        sideBar={sideBar}
        enableRangeSelection={true}
        overlayLoadingTemplate={
          '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
        }
        stopEditingWhenCellsLoseFocus={true}
        onFirstDataRendered={onFirstDataRendered}
        rowSelection="multiple"
        suppressRowClickSelection
        onSelectionChanged={onSelectionChanged}
      ></AgGridReact>
    </div>
  );
};

export default ContractReturnGrid;
